import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { leverageApi } from '../api';
import type {
  LeverageCampaign,
  LeverageCampaignPayload,
  LeveragePoolTotalPayload,
  LeveragePrizePool,
  LeverageTier,
  LeverageTierPayload,
} from '../types';
import { getDefaultCampaign, getDefaultPool, getErrorMessage, sortTiers } from '../utils';

export function useLeverage() {
  const mountedRef = useRef(true);

  const [campaign, setCampaign] = useState<LeverageCampaign>(getDefaultCampaign());
  const [pool, setPool] = useState<LeveragePrizePool>(getDefaultPool());
  const [tiers, setTiers] = useState<LeverageTier[]>([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p, t] = await Promise.all([
        leverageApi.getCampaign(),
        leverageApi.getPool(),
        leverageApi.listTiers(),
      ]);
      if (!mountedRef.current) return;
      setCampaign(c);
      setPool(p);
      setTiers(sortTiers(t));
    } catch (err) {
      if (mountedRef.current) setError(new Error(getErrorMessage(err)));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  const refetchCampaign = useCallback(async () => {
    const c = await leverageApi.getCampaign();
    if (mountedRef.current) setCampaign(c);
    return c;
  }, []);

  const refetchTiers = useCallback(async () => {
    const t = await leverageApi.listTiers();
    if (mountedRef.current) setTiers(sortTiers(t));
    return t;
  }, []);

  const refetchPool = useCallback(async () => {
    const p = await leverageApi.getPool();
    if (mountedRef.current) setPool(p);
    return p;
  }, []);

  const saveCampaign = useCallback(
    async (payload: LeverageCampaignPayload) => {
      setSaving(true);
      try {
        await leverageApi.setCampaign(payload);
        await refetchCampaign();
      } finally {
        if (mountedRef.current) setSaving(false);
      }
    },
    [refetchCampaign]
  );

  const upsertTier = useCallback(
    async (payload: LeverageTierPayload) => {
      setSaving(true);
      try {
        await leverageApi.upsertTier(payload);
        await refetchTiers();
      } finally {
        if (mountedRef.current) setSaving(false);
      }
    },
    [refetchTiers]
  );

  const deleteTier = useCallback(
    async (tierIndex: number) => {
      setSaving(true);
      try {
        await leverageApi.deleteTier(tierIndex);
        await refetchTiers();
      } finally {
        if (mountedRef.current) setSaving(false);
      }
    },
    [refetchTiers]
  );

  const setPoolTotal = useCallback(
    async (payload: LeveragePoolTotalPayload) => {
      setSaving(true);
      try {
        await leverageApi.setPoolTotal(payload);
        await refetchPool();
      } finally {
        if (mountedRef.current) setSaving(false);
      }
    },
    [refetchPool]
  );

  useEffect(() => {
    mountedRef.current = true;
    fetchAll();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchAll]);

  return useMemo(
    () => ({
      campaign,
      pool,
      tiers,
      loading,
      saving,
      error,
      refetch: fetchAll,
      refetchCampaign,
      refetchTiers,
      refetchPool,
      saveCampaign,
      upsertTier,
      deleteTier,
      setPoolTotal,
    }),
    [
      campaign,
      deleteTier,
      error,
      fetchAll,
      loading,
      pool,
      refetchCampaign,
      refetchPool,
      refetchTiers,
      saveCampaign,
      saving,
      setPoolTotal,
      tiers,
      upsertTier,
    ]
  );
}
